import React, { useState, Dispatch, SetStateAction, useEffect } from "react";
import { RequiredLabel } from "../../FormElements";
import styled from "styled-components";
import { PrimaryButtonStyled } from "../../PrimaryButtonStyled";
import { OutlinedButtonStyled } from "../../OutlinedButtonStyled";
import { useGlobalModal } from "../../../../contexts/GlobalModalContext";
import FileBrowser from "../../FileBrowser";
import { FileType } from "../GlobalModal";
import { uploadDtConsentForm } from "../../../../api/ConsentFormApi";
import { useAlert } from "../../../../contexts/AlertsContext";
import { getApiErrorMessage } from "../../../../util/ApiErrorUtil";

const ModalBodyStyle = styled.div.attrs(() => ({ className: "mt-4" }))`
    height: 260px;
    margin-left: 85px;
`;

type UploadDtConsentFormModalBodyProps = {
    dtUploadFile: FileType;
    setDtUploadFile: Dispatch<SetStateAction<FileType>>;
};

const UploadDtConsentFormModalBody: React.FC<UploadDtConsentFormModalBodyProps> = ({
    dtUploadFile,
    setDtUploadFile,
}) => {
    const onFileAdd = (file: any) => {
        if (file.length) {
            const fileToAdd: FileType = {
                name: file[0].name,
                fileBase64String: "",
                id: file[0].name + Math.floor(Math.random() * 100),
            };
            const reader = new FileReader();
            reader.onload = () => {
                fileToAdd.fileBase64String = reader.result;
                setDtUploadFile(fileToAdd);
            };
            reader.readAsDataURL(file[0]);
        }
    };

    const onFileDelete = () => {
        setDtUploadFile(null);
    };

    return (
        <ModalBodyStyle>
            <div>
                <RequiredLabel text="Drug Test Consent Form" required />
                <FileBrowser
                    onFileAdd={(file: any) => {
                        onFileAdd(file);
                    }}
                    file={dtUploadFile}
                    onFileDelete={onFileDelete}
                />
            </div>
        </ModalBodyStyle>
    );
};

type UploadDtConsentFormModalFooterProps = {
    personnelId: number;
    dtUploadFile: FileType;
    onSettled?: () => void;
};

const UploadDtConsentFormModalFooter: React.FC<UploadDtConsentFormModalFooterProps> = ({
    personnelId,
    dtUploadFile,
    onSettled,
}) => {
    const showAlert = useAlert();
    const { closeModal } = useGlobalModal();
    const [uploadDisabled, setUploadDisabled] = useState<boolean>(true);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        setUploadDisabled(!dtUploadFile);
    }, [dtUploadFile]);

    const onUploadClicked = async () => {
        if (uploading || !dtUploadFile) {
            return;
        }
        const drugTestConsentFormFileDTO = {
            fileName: dtUploadFile.name,
            fileBase64String: dtUploadFile.fileBase64String,
        };
        setUploading(true);
        try {
            await uploadDtConsentForm(personnelId, drugTestConsentFormFileDTO);
            showAlert("Uploaded", "success");
            closeModal();
            if (typeof onSettled === "function") {
                onSettled();
            }
        } catch (error) {
            showAlert(
                getApiErrorMessage(
                    error,
                    "Error while uploading Drug Test Consent Form, Please contact BIGS Systems",
                ),
                "danger",
            );
            if (typeof onSettled === "function") {
                onSettled();
            }
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="d-flex">
            <div className="mr-2">
                <PrimaryButtonStyled
                    width={82}
                    height={26}
                    disabled={uploadDisabled || uploading}
                    onClick={event => {
                        event.stopPropagation();
                        void onUploadClicked();
                    }}
                >
                    {uploading ? "Uploading…" : "Upload"}
                </PrimaryButtonStyled>
            </div>

            <OutlinedButtonStyled
                width={80}
                height={26}
                disabled={uploading}
                onClick={event => {
                    event.stopPropagation();
                    closeModal();
                }}
            >
                Cancel
            </OutlinedButtonStyled>
        </div>
    );
};

UploadDtConsentFormModalBody.displayName = "BodyContent";
UploadDtConsentFormModalFooter.displayName = "FooterContent";

export { UploadDtConsentFormModalBody, UploadDtConsentFormModalFooter };
